import { Loader2, SlidersHorizontal } from "lucide-react"
import type { TFunction } from "i18next"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/shared/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select"
import { cn } from "@/shared/lib/utils"
import { handleNumericKeyDown, sanitizeNumericInput } from "@/shared/lib/input-helpers"

import {
  ADVANCED_FIELDS,
  PRIMARY_FIELDS,
  type FormFieldDefinition,
  type NumberFieldDefinition,
  type SelectFieldDefinition,
} from "../constants"
import type { CalculatorFormErrors, CalculatorFormValues } from "../types"

type CalculatorFormProps = {
  values: CalculatorFormValues
  errors: CalculatorFormErrors
  isCalculating: boolean
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
  onFieldChange: (field: keyof CalculatorFormValues, value: string) => void
  advancedOpen: boolean
  onToggleAdvanced: () => void
  validationAnnouncement: string
}

const hasValidationError = (errors: CalculatorFormErrors, field: keyof CalculatorFormValues) =>
  Boolean(errors[field])

const renderNumberField = ({
  field,
  value,
  error,
  onChange,
  t,
  tabIndex,
}: {
  field: NumberFieldDefinition
  value: string
  error: boolean
  onChange: (nextValue: string) => void
  t: TFunction
  tabIndex: number
}) => {
  const placeholder = field.placeholderKey ? t(field.placeholderKey) : undefined
  const suffix = field.suffixKey ? t(field.suffixKey) : undefined
  const secondarySuffix = field.secondarySuffixKey ? t(field.secondarySuffixKey) : undefined
  const suffixClassName = "body-sm whitespace-nowrap text-[var(--color-text-secondary)]"

  return (
    <div className="flex items-center gap-2">
      <Input
        aria-invalid={error}
        aria-describedby={error ? `${field.name}-error` : undefined}
        autoComplete="off"
        className={cn(
          error && "border-destructive focus:ring-destructive focus-visible:ring-destructive",
        )}
        id={field.name}
        inputMode={field.inputMode}
        min={field.min}
        max={field.max}
        name={field.name}
        placeholder={placeholder}
        step={field.step}
        type="number"
        tabIndex={tabIndex}
        value={value}
        onKeyDown={handleNumericKeyDown}
        onChange={(event) => onChange(sanitizeNumericInput(event.target.value))}
      />
      {suffix ? <span className={suffixClassName}>{suffix}</span> : null}
      {secondarySuffix ? <span className={suffixClassName}>{secondarySuffix}</span> : null}
    </div>
  )
}

const renderSelectField = ({
  field,
  value,
  error,
  onChange,
  t,
  tabIndex,
}: {
  field: SelectFieldDefinition
  value: string
  error: boolean
  onChange: (nextValue: string) => void
  t: TFunction
  tabIndex: number
}) => {
  const placeholder = field.placeholderKey
    ? t(field.placeholderKey)
    : t("form.placeholders.generic")

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        aria-describedby={error ? `${field.name}-error` : undefined}
        aria-invalid={error}
        className={cn(
          error && "border-destructive focus:ring-destructive focus-visible:ring-destructive",
        )}
        tabIndex={tabIndex}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {field.options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            <span className="flex flex-col items-center leading-tight">
              <span>{t(option.labelKey)}</span>
              {option.helperKey ? (
                <span className="body-xs text-[var(--color-text-muted)]">
                  {t(option.helperKey)}
                </span>
              ) : null}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function CalculatorForm({
  values,
  errors,
  isCalculating,
  onSubmit,
  onFieldChange,
  advancedOpen,
  onToggleAdvanced,
  validationAnnouncement,
}: CalculatorFormProps) {
  const { t } = useTranslation()
  const advancedPanelRef = useRef<HTMLDivElement>(null)
  const [advancedPanelHeight, setAdvancedPanelHeight] = useState(0)
  const hasAdvancedFields = ADVANCED_FIELDS.length > 0

  useEffect(() => {
    const panel = advancedPanelRef.current

    if (!panel) {
      return
    }

    const measure = () => {
      setAdvancedPanelHeight(panel.scrollHeight)
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(panel)
    window.addEventListener("resize", measure)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [advancedOpen, values])

  const renderFieldRow = (field: FormFieldDefinition, isAdvancedField = false) => {
    const error = hasValidationError(errors, field.name)
    const value = values[field.name]
    const label = t(field.labelKey)
    const inputTabIndex = isAdvancedField && !advancedOpen ? -1 : 0

    return (
      <div className="contents" key={field.name}>
        <div className="flex items-center gap-2 pt-0 sm:pt-2">
          <Label htmlFor={field.name}>{label}</Label>
        </div>
        <div className="form-input-container stack-sm">
          {field.kind === "number"
            ? renderNumberField({
                field,
                value,
                error,
                onChange: (nextValue) => onFieldChange(field.name, nextValue),
                t,
                tabIndex: inputTabIndex,
              })
            : renderSelectField({
                field,
                value,
                error,
                onChange: (nextValue) => onFieldChange(field.name, nextValue),
                t,
                tabIndex: inputTabIndex,
              })}
          {error ? (
            <p className="field-error" id={`${field.name}-error`}>
              {errors[field.name] ? t(errors[field.name]!) : null}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  return (
    <Card aria-labelledby="calculator-form-heading">
      <CardHeader className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)]/60">
        <div className="stack-sm text-center">
          <CardTitle className="heading-sm" id="calculator-form-heading">
            {t("form.title")}
          </CardTitle>
          <CardDescription className="body-sm mx-auto max-w-md">
            {t("form.description")}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <form
          aria-describedby="form-validation-announcement"
          className="stack-lg"
          noValidate
          onSubmit={onSubmit}
        >
          <div className="mt-4 grid grid-cols-1 gap-y-4 sm:grid-cols-[auto_1fr] sm:gap-x-6 sm:gap-y-6">
            {PRIMARY_FIELDS.map((field) => renderFieldRow(field))}

            {hasAdvancedFields ? (
              <>
                <div className="sm:col-span-2">
                  <button
                    aria-controls="advanced-options-panel"
                    aria-expanded={advancedOpen}
                    className="flex w-full items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-3 body-sm font-semibold text-[var(--color-text-primary)] transition hover:bg-[var(--color-brand-50)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                    tabIndex={0}
                    type="button"
                    onClick={onToggleAdvanced}
                  >
                    <span className="flex items-center gap-2">
                      <SlidersHorizontal aria-hidden className="h-4 w-4" />
                      {t("form.advanced.label")}
                    </span>
                    <span className="eyebrow">
                      {advancedOpen ? t("form.advanced.hide") : t("form.advanced.show")}
                    </span>
                  </button>
                </div>
                <div
                  aria-hidden={!advancedOpen}
                  className={cn(
                    "grid grid-cols-1 gap-y-4 transition-[max-height,margin-top,opacity] duration-300 ease-in-out will-change-[max-height,opacity,margin-top] sm:grid-cols-[subgrid] sm:col-span-2 sm:gap-y-6",
                    advancedOpen
                      ? "overflow-visible opacity-100"
                      : "pointer-events-none overflow-visible opacity-0",
                  )}
                  style={{ maxHeight: advancedOpen ? advancedPanelHeight + 24 : 0 }}
                  id="advanced-options-panel"
                  ref={advancedPanelRef}
                >
                  {ADVANCED_FIELDS.map((field) => renderFieldRow(field, true))}
                </div>
              </>
            ) : null}
          </div>

          <div className="stack-sm pt-2 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
            <Button
              animated={!isCalculating}
              disabled={isCalculating}
              size="lg"
              tabIndex={0}
              type="submit"
            >
              {isCalculating ? (
                <>
                  <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                  {t("form.buttons.calculating")}
                </>
              ) : (
                t("form.buttons.calculate")
              )}
            </Button>
          </div>
          <div
            aria-live="polite"
            className="sr-only"
            id="form-validation-announcement"
            role="status"
          >
            {validationAnnouncement}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
